/**
 * QuizBankService - Quản lý và cache danh sách trắc nghiệm có sẵn
 * Tự động scan và lưu vào biến tạm để trang TracNghiem có thể lọc nhanh
 */

export interface QuizBankItem {
  id: string
  lop: number
  chuong: number
  bai: number
  tenBai: string
  loai: 'BaiHoc' | 'KTGKI' | 'KTCKI' | 'KTGKII' | 'KTCKII' | 'OnThiTN'
  loaiLabel: string
  questionTypes: {
    TN: { count: number; dbPath: string; exists: boolean }
    DS: { count: number; dbPath: string; exists: boolean }
    TL: { count: number; dbPath: string; exists: boolean }
  }
  totalQuestions: number
  hasQuestions: boolean
  quizUrl: string
}

import { API_CONFIG } from '../../config/api.config'
import { quizApi } from '../api/quizApi'

class QuizBankService {
  // Biến tạm lưu trữ danh sách trắc nghiệm đã scan
  private cachedQuizzes: QuizBankItem[] = []
  private isScanned: boolean = false
  private isScanning: boolean = false
  private scanPromise: Promise<QuizBankItem[]> | null = null

  /**
   * Danh sách tất cả các bài có thể có (sẽ scan tất cả) - KHÔI PHỤC LOGIC CŨ
   */
  private getAllPossibleLocations() {
    const locations: Array<{
      lop: number
      chuong: number
      bai: number
      tenBai: string
    }> = []

    // ✅ KHÔI PHỤC: Cấu hình tự động cho TẤT CẢ các bài (giống logic cũ)
    const classConfig = [
      { lop: 10, maxBai: 20, maxChuong: 4 },
      { lop: 11, maxBai: 18, maxChuong: 4 },
      { lop: 12, maxBai: 22, maxChuong: 4 }
    ]

    // ✅ KHÔI PHỤC: Scan TẤT CẢ các bài trong tất cả các lớp, chương (logic cũ)
    for (const config of classConfig) {
      for (let chuong = 1; chuong <= config.maxChuong; chuong++) {
        for (let bai = 1; bai <= config.maxBai; bai++) {
          locations.push({
            lop: config.lop,
            chuong: chuong,
            bai: bai,
            tenBai: `Lớp ${config.lop} - Chương ${chuong} - Bài ${bai}`
          })
        }
      }
    }

    console.log(`📊 [QuizBankService] Generated ${locations.length} possible locations to scan`)
    return locations
  }

  /**
   * Kiểm tra xem database có tồn tại không (sử dụng HEAD request)
   */
  private async checkDatabaseExists(dbPath: string): Promise<boolean> {
    try {
      // Check JSON first
      const response = await fetch(dbPath, { method: 'GET' })
      if (response.ok) return true

      // Fallback to DB check
      const dbUrl = dbPath.replace('.json', '.db')
      const dbResponse = await fetch(dbUrl, { method: 'GET' })
      return dbResponse.ok
    } catch (error) {
      console.error(`❌ Error checking ${dbPath}: `, error)
      return false
    }
  }

  /**
   * Khởi tạo sẵn khi service được tạo
   * Tự động scan ngay khi import
   */
  constructor() {
    // ✅ FIX: Clear cache cũ để đảm bảo scan lại với logic mới
    console.log('🚀 QuizBankService v1.2 initialized (Fix: questions_tn.json + GET), clearing old cache and starting auto-scan...')
    this.isScanned = false
    this.cachedQuizzes = []
    this.isScanning = false
    this.scanPromise = null

    // Tự động scan khi service được khởi tạo
    this.scanAndCacheQuizzes().catch(error => {
      console.error('❌ Auto-scan failed:', error)
    })
  }

  /**
   * Lấy trạng thái scan
   */
  getScanStatus() {
    return {
      isScanned: this.isScanned,
      isScanning: this.isScanning,
      cachedCount: this.cachedQuizzes.length,
      progress: this.isScanned ? 100 : (this.isScanning ? 50 : 0)
    }
  }

  /**
   * Lấy label cho loại trắc nghiệm
   */
  private getLoaiLabel(loai: string): string {
    switch (loai) {
      case 'BaiHoc': return 'Trắc nghiệm bài học'
      case 'KTGKI': return 'Kiểm tra giữa kì I'
      case 'KTCKI': return 'Kiểm tra cuối kì I'
      case 'KTGKII': return 'Kiểm tra giữa kì II'
      case 'KTCKII': return 'Kiểm tra cuối kì II'
      case 'OnThiTN': return 'Ôn thi tốt nghiệp'
      default: return loai
    }
  }


  /**
   * Tạo đường dẫn database
   */
  private getDbPath(lop: number, chuong: number, bai: number, loai: string): string {
    const fileName = `questions_${loai.toLowerCase()}.json`
    return `${API_CONFIG.bank.baseUrl}/L${lop}/C${chuong}/B${String(bai).padStart(2, '0')}/${loai}/${fileName}`
  }

  /**
   * Scan tất cả các trắc nghiệm có sẵn và lưu vào cache (OPTIMIZED)
   * Sử dụng manifest để loại bỏ network requests
   */
  async scanAndCacheQuizzes(forceRescan: boolean = false): Promise<QuizBankItem[]> {
    // ✅ OPTIMIZATION: Use Python Backend API if enabled
    if (API_CONFIG.features.usePythonBackend) {
      console.log('🚀 [QuizBankService] Using Python Backend for Quiz Scan')
      try {
        if (this.cachedQuizzes.length > 0 && !forceRescan) {
          return this.cachedQuizzes;
        }

        this.isScanning = true;
        const scanResult = await quizApi.scanQuizzes();

        // Map Python backend response to QuizBankItem format
        // Note: The structure is already very similar due to shared interfaces
        const quizzes: QuizBankItem[] = scanResult.quizzes.map(q => ({
          id: q.quizUrl.replace('/trac-nghiem/', '').replace(/\//g, '_'), // Ensure ID format matches expectations
          lop: q.lop,
          chuong: q.chuong,
          bai: q.bai,
          tenBai: q.title,
          loai: 'BaiHoc', // Default for now, backend logic might need expansion if other types supported
          loaiLabel: 'Trắc nghiệm bài học',
          questionTypes: {
            TN: {
              count: q.questionTypes.TN?.count || 0,
              dbPath: q.questionTypes.TN?.dbPath || '',
              exists: q.questionTypes.TN?.exists || false
            },
            DS: {
              count: q.questionTypes.DS?.count || 0,
              dbPath: q.questionTypes.DS?.dbPath || '',
              exists: q.questionTypes.DS?.exists || false
            },
            TL: {
              count: q.questionTypes.TL?.count || 0,
              dbPath: q.questionTypes.TL?.dbPath || '',
              exists: q.questionTypes.TL?.exists || false
            }
          },
          totalQuestions: q.totalQuestions,
          hasQuestions: q.totalQuestions > 0,
          quizUrl: q.quizUrl
        }));

        this.cachedQuizzes = quizzes;
        this.isScanned = true;
        this.isScanning = false;
        console.log(`✅ [QuizBankService] Loaded ${quizzes.length} quizzes from Python API`);
        return quizzes;
      } catch (error) {
        console.error('❌ [QuizBankService] Python Scan Failed:', error);
        this.isScanning = false;
        // Fallback to manual scan if Python fails? Or just return empty?
        // Let's fallback to manual scan for robustness
        console.warn('⚠️ Falling back to manual client-side scan...');
      }
    }
    // ✅ FIX: Nếu force rescan, clear cache cũ
    if (forceRescan) {
      console.log('🔄 Force rescan requested, clearing cache...')
      this.isScanned = false
      this.cachedQuizzes = []
      this.isScanning = false
      this.scanPromise = null
    }

    // Nếu đang scan, đợi kết quả scan hiện tại
    if (this.isScanning && this.scanPromise) {
      console.log('⏳ Scan đang chạy, đợi kết quả...')
      return this.scanPromise
    }

    // Nếu đã scan rồi và không force rescan, trả về cache
    if (this.isScanned && this.cachedQuizzes.length > 0 && !forceRescan) {
      console.log('✅ Sử dụng cache, có', this.cachedQuizzes.length, 'bài trắc nghiệm')
      return this.cachedQuizzes
    }

    // Bắt đầu scan mới
    console.log('🔄 [QuizBankService] Bắt đầu scan trắc nghiệm (KHÔI PHỤC LOGIC CŨ - Scan tất cả các bài)...')
    console.log('🔄 [QuizBankService] Force rescan:', forceRescan, 'isScanned:', this.isScanned, 'cachedCount:', this.cachedQuizzes.length)
    this.isScanning = true

    this.scanPromise = (async () => {
      const startTime = Date.now()

      // ✅ KHÔI PHỤC: Lấy TẤT CẢ các locations có thể có (giống logic cũ)
      const allLocations = this.getAllPossibleLocations()
      console.log(`📊 [QuizBankService] ✅ Generated ${allLocations.length} possible locations to scan`)
      console.log(`📊 [QuizBankService] First 5 locations:`, allLocations.slice(0, 5))
      console.log(`📊 [QuizBankService] Last 5 locations:`, allLocations.slice(-5))

      // ✅ KHÔI PHỤC: Chia thành batches để xử lý song song (giống logic cũ)
      const BATCH_SIZE = 50 // Xử lý 50 bài mỗi batch
      const batches: Array<typeof allLocations> = []
      for (let i = 0; i < allLocations.length; i += BATCH_SIZE) {
        batches.push(allLocations.slice(i, i + BATCH_SIZE))
      }

      console.log(`📊 [QuizBankService] Processing ${batches.length} batches of ${BATCH_SIZE} locations each`)

      let processedCount = 0
      const allQuizResults: Array<QuizBankItem | null> = []

      // ✅ KHÔI PHỤC: Xử lý từng batch song song (giống logic cũ)
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex]
        const batchStartTime = Date.now()

        // Scan song song tất cả locations trong batch
        const batchResults = await Promise.all(
          batch.map(async (location) => {
            try {
              // ✅ KHÔI PHỤC: Kiểm tra song song cả 3 loại câu hỏi (sử dụng HEAD request)
              const [tnExists, dsExists, tlExists] = await Promise.all([
                this.checkDatabaseExists(this.getDbPath(location.lop, location.chuong, location.bai, 'TN')),
                this.checkDatabaseExists(this.getDbPath(location.lop, location.chuong, location.bai, 'DS')),
                this.checkDatabaseExists(this.getDbPath(location.lop, location.chuong, location.bai, 'TL'))
              ])

              // Chỉ đếm số câu nếu có ít nhất 1 database tồn tại
              if (tnExists || dsExists || tlExists) {
                // Import bankQuestionService để đếm số câu thực tế
                const { bankQuestionService } = await import('../bankQuestionService_moi')

                // ✅ FIX: Chỉ đếm từ database hệ thống, KHÔNG load database của giáo viên
                // Không truyền teacherId và teacherDatabases để đảm bảo chỉ đếm từ hệ thống
                const counts = await bankQuestionService.getQuestionCounts(
                  String(location.lop),
                  String(location.chuong),
                  String(location.bai),
                  null, // ✅ FIX: Explicitly set to null (not undefined) to ensure system-only mode
                  null  // ✅ FIX: Explicitly set to null (not undefined) to ensure system-only mode
                )

                const questionTypes = {
                  TN: {
                    count: counts.TN || 0,
                    dbPath: this.getDbPath(location.lop, location.chuong, location.bai, 'TN'),
                    exists: tnExists && counts.TN > 0
                  },
                  DS: {
                    count: counts.DS || 0,
                    dbPath: this.getDbPath(location.lop, location.chuong, location.bai, 'DS'),
                    exists: dsExists && counts.DS > 0
                  },
                  TL: {
                    count: counts.TL || 0,
                    dbPath: this.getDbPath(location.lop, location.chuong, location.bai, 'TL'),
                    exists: tlExists && counts.TL > 0
                  }
                }

                const totalQuestions = questionTypes.TN.count + questionTypes.DS.count + questionTypes.TL.count

                if (totalQuestions > 0) {
                  return {
                    id: `L${location.lop}_C${location.chuong}_B${String(location.bai).padStart(2, '0')}`,
                    lop: location.lop,
                    chuong: location.chuong,
                    bai: location.bai,
                    tenBai: location.tenBai,
                    loai: 'BaiHoc' as const,
                    loaiLabel: this.getLoaiLabel('BaiHoc'),
                    questionTypes,
                    totalQuestions,
                    hasQuestions: true,
                    quizUrl: `/trac-nghiem/${location.lop}/${location.chuong}/${location.bai}`
                  }
                }
              }
            } catch (error) {
              console.warn(`⚠️ Error scanning L${location.lop}_C${location.chuong}_B${location.bai}:`, error)
            }

            return null
          })
        )

        allQuizResults.push(...batchResults)
        processedCount += batch.length

        const batchEndTime = Date.now()
        const batchDuration = ((batchEndTime - batchStartTime) / 1000).toFixed(2)
        console.log(`✅ [QuizBankService] Batch ${batchIndex + 1}/${batches.length} completed in ${batchDuration}s (${processedCount}/${allLocations.length} processed)`)

        // ✅ KHÔI PHỤC: Cập nhật progress (giống logic cũ)
        const progress = Math.round((processedCount / allLocations.length) * 100)
        console.log(`📊 [QuizBankService] Progress: ${progress}% (${processedCount}/${allLocations.length})`)
      }

      // ✅ KHÔI PHỤC: Filter out null results
      const quizResults = allQuizResults

      // ✅ KHÔI PHỤC: Filter out null results
      const quizzes = quizResults.filter(item => item !== null) as QuizBankItem[]

      // Lưu vào cache
      this.cachedQuizzes = quizzes
      this.isScanned = true
      this.isScanning = false
      this.scanPromise = null

      const endTime = Date.now()
      const duration = ((endTime - startTime) / 1000).toFixed(2)

      console.log(`✅ [QuizBankService] SCAN HOÀN TẤT trong ${duration}s!`)
      console.log(`📊 [QuizBankService] Tìm thấy ${quizzes.length} bài trắc nghiệm từ ${allLocations.length} locations`)
      console.log(`⚡ [QuizBankService] Tốc độ: ${(allLocations.length / parseFloat(duration)).toFixed(1)} locations/s`)
      console.log('📋 [QuizBankService] Cached quizzes:', quizzes)

      // Thống kê chi tiết
      const stats = {
        byClass: {} as Record<number, number>,
        byQuestionType: { TN: 0, DS: 0, TL: 0 },
        totalQuestions: 0
      }

      quizzes.forEach(quiz => {
        stats.byClass[quiz.lop] = (stats.byClass[quiz.lop] || 0) + 1
        if (quiz.questionTypes.TN.exists) stats.byQuestionType.TN++
        if (quiz.questionTypes.DS.exists) stats.byQuestionType.DS++
        if (quiz.questionTypes.TL.exists) stats.byQuestionType.TL++
        stats.totalQuestions += quiz.totalQuestions
      })

      console.log('📊 Thống kê:', stats)

      // Fetch PMaker Published Exams
      try {
        const pmakerResponse = await fetch(`${API_CONFIG.python.apiUrl}/pmaker-advanced/published-exams`);
        if (pmakerResponse.ok) {
          const pmakerData = await pmakerResponse.json();
          if (pmakerData && pmakerData.data && Array.isArray(pmakerData.data)) {
            const pmakerExams = pmakerData.data.map((exam: any) => {
              const settings = exam.settings || {};
              const targetCategory = settings.target_category || settings.exam_type || 'BaiHoc';
              let targetLevelStr = settings.target_level || settings.target_class || '12';
              if (targetLevelStr === 'all') targetLevelStr = '12';
              const lopNum = parseInt(targetLevelStr) || 12;
              let targetLessonStr = settings.target_lesson || '1';
              if (targetLessonStr === 'all') targetLessonStr = '1';
              const baiNum = parseInt(targetLessonStr) || 1;

              return {
                id: exam.id,
                lop: lopNum,
                chuong: 1, // Default or parsed if needed
                bai: baiNum,
                tenBai: exam.title || `Bài ${baiNum}`,
                loai: targetCategory as any,
                loaiLabel: this.getLoaiLabel(targetCategory),
                questionTypes: {
                  TN: { count: 0, dbPath: '', exists: true }, // Mark exists to allow click
                  DS: { count: 0, dbPath: '', exists: false },
                  TL: { count: 0, dbPath: '', exists: false }
                },
                totalQuestions: exam.total_points || 10,
                hasQuestions: true,
                quizUrl: `/exam/${exam.slug || exam.id}`
              };
            });
            console.log(`✅ [QuizBankService] Fetched ${pmakerExams.length} PMaker exams`);
            quizzes.push(...pmakerExams);
            this.cachedQuizzes = quizzes; // Update cache again
          }
        }
      } catch (e) {
        console.error('❌ Failed to fetch PMaker exams:', e);
      }

      return quizzes
    })()

    return this.scanPromise
  }

  /**
   * Lấy tất cả trắc nghiệm từ cache (tự động scan nếu chưa có)
   */
  async getAllQuizzes(): Promise<QuizBankItem[]> {
    if (!this.isScanned) {
      return await this.scanAndCacheQuizzes()
    }
    return this.cachedQuizzes
  }

  /**
   * Lọc trắc nghiệm theo điều kiện
   */
  async filterQuizzes(filters: {
    loai?: 'BaiHoc' | 'KTGKI' | 'KTCKI' | 'KTGKII' | 'KTCKII' | 'OnThiTN'
    lop?: number
    chuong?: number
    bai?: number
    searchTerm?: string
  }): Promise<QuizBankItem[]> {
    const allQuizzes = await this.getAllQuizzes()
    let filtered = [...allQuizzes]

    // Lọc theo loại
    if (filters.loai) {
      filtered = filtered.filter(q => q.loai === filters.loai)
    }

    // Lọc theo lớp
    if (filters.lop) {
      filtered = filtered.filter(q => q.lop === filters.lop)
    }

    // Lọc theo chương
    if (filters.chuong) {
      filtered = filtered.filter(q => q.chuong === filters.chuong)
    }

    // Lọc theo bài
    if (filters.bai) {
      filtered = filtered.filter(q => q.bai === filters.bai)
    }

    // Lọc theo từ khóa tìm kiếm
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase()
      filtered = filtered.filter(q =>
        q.tenBai.toLowerCase().includes(searchLower) ||
        q.loaiLabel.toLowerCase().includes(searchLower) ||
        `lớp ${q.lop}`.toLowerCase().includes(searchLower) ||
        `bài ${q.bai}`.toLowerCase().includes(searchLower)
      )
    }

    console.log(`🔍 Filter result: ${filtered.length}/${allQuizzes.length} bài trắc nghiệm`)
    return filtered
  }

  /**
   * Lấy trắc nghiệm theo ID
   */
  async getQuizById(id: string): Promise<QuizBankItem | null> {
    const allQuizzes = await this.getAllQuizzes()
    return allQuizzes.find(q => q.id === id) || null
  }

  /**
   * Lấy số lượng trắc nghiệm theo lớp
   */
  async getQuizCountByClass(lop: number): Promise<number> {
    const allQuizzes = await this.getAllQuizzes()
    return allQuizzes.filter(q => q.lop === lop).length
  }


  /**
   * Làm mới cache (force rescan)
   */
  async refreshCache(): Promise<QuizBankItem[]> {
    console.log('🔄 Refreshing cache...')
    this.isScanned = false
    this.cachedQuizzes = []
    this.isScanning = false
    this.scanPromise = null
    return await this.scanAndCacheQuizzes(true) // ✅ FIX: Force rescan
  }

  /**
   * Lấy thống kê tổng quan
   */
  async getStatistics(): Promise<{
    total: number
    byClass: { [key: number]: number }
    byQuestionType: { TN: number; DS: number; TL: number }
    totalQuestions: number
  }> {
    const allQuizzes = await this.getAllQuizzes()

    const byClass: { [key: number]: number } = {}
    const byQuestionType = { TN: 0, DS: 0, TL: 0 }
    let totalQuestions = 0

    for (const quiz of allQuizzes) {
      // Đếm theo lớp
      byClass[quiz.lop] = (byClass[quiz.lop] || 0) + 1

      // Đếm theo loại câu hỏi
      if (quiz.questionTypes.TN.exists) byQuestionType.TN++
      if (quiz.questionTypes.DS.exists) byQuestionType.DS++
      if (quiz.questionTypes.TL.exists) byQuestionType.TL++

      totalQuestions += quiz.totalQuestions
    }

    return {
      total: allQuizzes.length,
      byClass,
      byQuestionType,
      totalQuestions
    }
  }

  /**
   * Làm mới cache để scan lại
   * (Sử dụng khi có thêm trắc nghiệm mới)
   */
  async rescan(): Promise<QuizBankItem[]> {
    console.log('🔄 Rescanning all databases...')
    this.isScanned = false
    this.cachedQuizzes = []
    this.isScanning = false
    this.scanPromise = null
    return await this.scanAndCacheQuizzes(true) // ✅ FIX: Force rescan
  }
}

// Export singleton instance
export const quizBankService = new QuizBankService()
export default quizBankService

